<template>
  <div>
    <!-- Dispatch Modal -->
    <b-modal id="dispatch-modal" title="Dispatch Details" hide-footer size="md">
      <b-form @submit.prevent="saveDispatch">
        <b-form-group label="Vehicle Number">
          <b-form-input v-model="dispatchForm.vehicle_number" placeholder="MH12AB1234" />
        </b-form-group>
        <b-form-group label="Driver Name">
          <b-form-input v-model="dispatchForm.driver_name" />
        </b-form-group>
        <b-form-group label="Driver Phone">
          <b-form-input v-model="dispatchForm.driver_phone" type="tel" />
        </b-form-group>
        <b-form-group label="Challan Number">
          <b-form-input v-model="dispatchForm.challan_number" />
        </b-form-group>
        <b-form-group label="Challan File (optional)">
          <b-form-file v-model="dispatchForm.challan_file" accept=".pdf,.jpg,.jpeg,.png" />
        </b-form-group>
        <b-form-group label="GST Number (optional)">
          <b-form-input v-model="dispatchForm.gst_number" />
        </b-form-group>
        <b-form-group label="Estimated Arrival">
          <b-form-input v-model="dispatchForm.estimated_arrival" type="datetime-local" />
        </b-form-group>
        <b-button type="submit" variant="primary" :disabled="saving" block>
          {{ saving ? 'Saving…' : 'Save & Mark Dispatched' }}
        </b-button>
        <b-button
          v-if="dispatchBooking && dispatchBooking.status === 'dispatched'"
          variant="success" class="mt-2" block :disabled="markingDelivered"
          @click="markDelivered"
        >
          ✓ Mark as Delivered
        </b-button>
      </b-form>
    </b-modal>

    <!-- Reject reason modal -->
    <b-modal id="reject-modal" title="Reject Booking" @ok="confirmReject">
      <b-form-group label="Rejection Reason (optional)">
        <b-form-textarea v-model="rejectReason" rows="3" placeholder="Reason for rejection…" />
      </b-form-group>
    </b-modal>

    <!-- Header -->
    <div class="bk-header">
      <div>
        <h3 class="bk-title">Bookings</h3>
        <p class="bk-sub">Orders buyers placed against your listings.</p>
      </div>
      <b-button variant="outline-primary" size="sm" @click="fetchBookings">
        <feather-icon icon="RefreshCwIcon" size="14" class="mr-25" /> Refresh
      </b-button>
    </div>

    <!-- Status filter chips -->
    <div class="bk-chips">
      <span
        v-for="opt in statusOptions"
        :key="opt.value"
        class="bk-chip"
        :class="{ active: statusFilter === opt.value }"
        @click="statusFilter = opt.value"
      >{{ opt.text }}<span v-if="countFor(opt.value)" class="bk-chip-n">{{ countFor(opt.value) }}</span></span>
    </div>

    <!-- Loading -->
    <b-row v-if="loading">
      <b-col v-for="n in 3" :key="n" md="6" lg="4">
        <b-card><b-skeleton width="55%" /><b-skeleton width="75%" class="mt-1" /><b-skeleton width="40%" class="mt-2" /></b-card>
      </b-col>
    </b-row>

    <!-- Empty -->
    <b-card v-else-if="!filtered.length" class="bk-empty text-center">
      <b-img :src="require('@/assets/images/svg/infographics/no_data_infographic.svg')" class="bk-empty-img" />
      <h4 class="mt-2">No bookings{{ statusFilter !== 'all' ? ' in this status' : '' }}</h4>
      <p class="text-muted mb-0">Orders from buyers will appear here.</p>
    </b-card>

    <!-- Booking cards -->
    <b-row v-else>
      <b-col v-for="item in filtered" :key="item.id" md="6" lg="4">
        <div class="bk-card">
          <div class="bk-top">
            <div>
              <span class="bk-bird">{{ item.listing && item.listing.bird_type ? item.listing.bird_type.toUpperCase() : 'BIRDS' }}</span>
              <div class="bk-date">{{ (item.created_at || '').substring(0, 10) }}</div>
            </div>
            <b-badge :variant="statusPill(item.status)" pill class="bk-status">{{ prettyStatus(item.status) }}</b-badge>
          </div>

          <div class="bk-buyer">
            <feather-icon icon="UserIcon" size="14" class="text-muted mr-25" />
            <strong>{{ item.buyer ? item.buyer.name : '—' }}</strong>
            <span v-if="item.buyer && item.buyer.phone" class="bk-phone">· {{ item.buyer.phone }}</span>
          </div>

          <div class="bk-amount">
            <div>
              <span class="bk-total">₹{{ Number(item.total_amount).toLocaleString('en-IN') }}</span>
              <b-badge v-if="item.offered_price_per_kg" variant="light-warning" class="ml-50">Buyer offer</b-badge>
            </div>
            <div class="bk-rate">{{ item.qty_kg }} kg × ₹{{ item.locked_price_per_kg }}/kg</div>
          </div>

          <div class="bk-divider" />
          <div class="bk-actions">
            <b-button v-if="item.status === 'pending'" size="sm" variant="success" class="mr-50" @click="approve(item)">
              <feather-icon icon="CheckIcon" size="14" /> Approve
            </b-button>
            <b-button v-if="item.status === 'pending'" size="sm" variant="outline-danger" class="mr-50" @click="openReject(item)">
              <feather-icon icon="XIcon" size="14" /> Reject
            </b-button>
            <b-button v-if="item.status === 'confirmed' || item.status === 'dispatched'" size="sm" variant="primary" @click="openDispatch(item)">
              <feather-icon icon="TruckIcon" size="14" /> {{ item.status === 'dispatched' ? 'Update dispatch' : 'Dispatch' }}
            </b-button>
            <span v-if="item.status === 'delivered'" class="bk-done"><feather-icon icon="CheckCircleIcon" size="14" /> Delivered</span>
            <span v-if="item.status === 'rejected'" class="bk-rejected"><feather-icon icon="SlashIcon" size="14" /> Rejected</span>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import axios from '@/libs/axios'

export default {
  name: 'MarketBookingList',
  data() {
    return {
      bookings: [],
      loading: true,
      saving: false,
      markingDelivered: false,
      statusFilter: 'all',
      statusOptions: [
        { value: 'all',       text: 'All' },
        { value: 'pending',   text: 'Pending' },
        { value: 'confirmed', text: 'Confirmed' },
        { value: 'dispatched',text: 'Dispatched' },
        { value: 'delivered', text: 'Delivered' },
        { value: 'rejected',  text: 'Rejected' },
      ],
      fields: [
        { key: 'buyer',   label: 'Buyer' },
        { key: 'listing', label: 'Bird' },
        { key: 'amount',  label: 'Amount' },
        { key: 'status',  label: 'Status' },
        { key: 'created_at', label: 'Date', formatter: v => v?.substring(0, 10) },
        { key: 'actions', label: '' },
      ],
      dispatchBooking: null,
      dispatchForm: {
        vehicle_number: '', driver_name: '', driver_phone: '',
        challan_number: '', challan_file: null, gst_number: '', estimated_arrival: '',
      },
      rejectBooking: null,
      rejectReason: '',
    }
  },
  computed: {
    filtered() {
      if (this.statusFilter === 'all') return this.bookings
      return this.bookings.filter(b => b.status === this.statusFilter)
    },
  },
  created() {
    this.fetchBookings()
  },
  methods: {
    async fetchBookings() {
      this.loading = true
      try {
        const { data } = await axios.get('web/market-bookings')
        this.bookings = data?.data ?? data ?? []
      } finally {
        this.loading = false
      }
    },
    async approve(item) {
      const ok = await this.$bvModal.msgBoxConfirm(`Approve ${item.qty_kg} kg booking from ${item.buyer?.name}?`, { okVariant: 'success', okTitle: 'Approve' })
      if (!ok) return
      try {
        await axios.post(`web/market-bookings/${item.id}/approve`)
        this.fetchBookings()
      } catch (e) {
        const msg = e?.response?.data?.message ?? 'Failed to approve booking'
        this.$bvToast.toast(msg, { title: 'Error', variant: 'danger', solid: true })
      }
    },
    openReject(item) {
      this.rejectBooking = item
      this.rejectReason = ''
      this.$bvModal.show('reject-modal')
    },
    async confirmReject() {
      try {
        await axios.post(`web/market-bookings/${this.rejectBooking.id}/reject`, { rejection_reason: this.rejectReason || undefined })
        this.$bvModal.hide('reject-modal')
        this.fetchBookings()
      } catch (e) {
        const msg = e?.response?.data?.message ?? 'Failed to reject booking'
        this.$bvToast.toast(msg, { title: 'Error', variant: 'danger', solid: true })
      }
    },
    openDispatch(item) {
      this.dispatchBooking = item
      const d = item.dispatch ?? {}
      this.dispatchForm = {
        vehicle_number:    d.vehicle_number ?? '',
        driver_name:       d.driver_name ?? '',
        driver_phone:      d.driver_phone ?? '',
        challan_number:    d.challan_number ?? '',
        challan_file:      null,
        gst_number:        d.gst_number ?? '',
        estimated_arrival: d.estimated_arrival ?? '',
      }
      this.$bvModal.show('dispatch-modal')
    },
    async saveDispatch() {
      this.saving = true
      try {
        const fd = new FormData()
        Object.entries(this.dispatchForm).forEach(([k, v]) => {
          // V14: skip only null/undefined, not empty strings (empty strings are valid resets)
          if (k !== 'challan_file' && v != null) fd.append(k, v)
        })
        if (this.dispatchForm.challan_file) fd.append('challan_file', this.dispatchForm.challan_file)
        await axios.post(`web/market-bookings/${this.dispatchBooking.id}/dispatch`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        this.$bvModal.hide('dispatch-modal')
        this.fetchBookings()
      } catch (e) {
        const msg = e?.response?.data?.message ?? 'Failed to save dispatch'
        this.$bvToast.toast(msg, { title: 'Error', variant: 'danger', solid: true })
      } finally {
        this.saving = false
      }
    },
    async markDelivered() {
      const ok = await this.$bvModal.msgBoxConfirm('Mark this order as delivered?', { okTitle: 'Yes, Delivered', okVariant: 'success' })
      if (!ok) return
      this.markingDelivered = true
      try {
        await axios.post(`web/market-bookings/${this.dispatchBooking.id}/delivered`)
        this.$bvModal.hide('dispatch-modal')
        this.fetchBookings()
      } catch (e) {
        const msg = e?.response?.data?.message ?? 'Failed to mark delivered'
        this.$bvToast.toast(msg, { title: 'Error', variant: 'danger', solid: true })
      } finally {
        this.markingDelivered = false
      }
    },
    statusVariant(s) {
      return { pending: 'warning', confirmed: 'success', rejected: 'danger', dispatched: 'primary', delivered: 'secondary' }[s] ?? 'secondary'
    },
    statusPill(s) {
      return {
        pending: 'light-warning', confirmed: 'light-success', dispatched: 'light-primary',
        delivered: 'light-secondary', rejected: 'light-danger',
      }[s] || 'light-secondary'
    },
    prettyStatus(s) {
      return { pending: 'Pending', confirmed: 'Confirmed', dispatched: 'Dispatched', delivered: 'Delivered', rejected: 'Rejected' }[s] || s
    },
    countFor(status) {
      if (status === 'all') return this.bookings.length
      return this.bookings.filter(b => b.status === status).length
    },
  },
}
</script>

<style scoped>
.bk-header { display: flex; align-items: flex-end; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; flex-wrap: wrap; }
.bk-title { font-weight: 700; margin-bottom: 2px; }
.bk-sub { margin: 0; color: #6e6b7b; font-size: 0.9rem; }
.bk-chips { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-bottom: 1.2rem; }
.bk-chip { font-size: 0.8rem; font-weight: 600; padding: 0.35rem 0.8rem; border-radius: 20px; background: rgba(34,41,47,.06); color: #6e6b7b; cursor: pointer; transition: all .15s ease; }
.bk-chip:hover { background: rgba(41,109,180,.12); }
.bk-chip.active { background: #296DB4; color: #fff; }
.bk-chip-n { margin-left: 0.35rem; opacity: .8; font-weight: 700; }
.bk-card { background: var(--white,#fff); border: 1px solid rgba(34,41,47,.08); border-radius: .7rem; padding: 1.1rem 1.15rem; margin-bottom: 1.4rem; box-shadow: 0 2px 10px rgba(34,41,47,.05); transition: box-shadow .2s ease, transform .2s ease; }
.bk-card:hover { box-shadow: 0 6px 20px rgba(34,41,47,.1); transform: translateY(-2px); }
.bk-top { display: flex; justify-content: space-between; align-items: flex-start; }
.bk-bird { font-size: 1.02rem; font-weight: 700; letter-spacing: .02em; }
.bk-date { font-size: .74rem; color: #6e6b7b; margin-top: 2px; }
.bk-status { font-size: .68rem; }
.bk-buyer { font-size: .88rem; margin: .7rem 0 .1rem; }
.bk-phone { color: #6e6b7b; font-size: .8rem; }
.bk-amount { margin-top: .6rem; }
.bk-total { font-size: 1.5rem; font-weight: 800; color: #296DB4; font-variant-numeric: tabular-nums; }
.bk-rate { font-size: .78rem; color: #6e6b7b; margin-top: 2px; }
.bk-divider { height: 1px; background: rgba(34,41,47,.08); margin: .85rem 0 .7rem; }
.bk-actions { display: flex; flex-wrap: wrap; align-items: center; gap: .3rem; }
.bk-done { color: #28c76f; font-weight: 600; font-size: .82rem; }
.bk-rejected { color: #ea5455; font-weight: 600; font-size: .82rem; }
.bk-empty { padding: 2.5rem 1rem; }
.bk-empty-img { max-width: 180px; opacity: .9; }
</style>
